import { useState, useEffect, useCallback } from 'react';
import { initEcho } from 'config/echo';
import { useAuth } from 'features/auth/context/AuthContext';
import axiosServices from 'utils/axios';

export interface Notification {
  id: string;
  type: string;
  notifiable_type: string;
  notifiable_id: number;
  channel: string;
  title: string;
  body: string;
  data?: Record<string, unknown>;
  read_at: string | null;
  saved_at?: string | null;
  created_at: string;
}

export interface NotificationMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  unread_count: number;
}

interface UseNotificationsOptions {
  status?: 'all' | 'read' | 'unread';
  channel?: string;
  perPage?: number;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  meta: NotificationMeta | null;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsSaved: (id: string) => Promise<void>;
  markAsUnsaved: (id: string) => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  setFilters: (options: UseNotificationsOptions) => void;
  refresh: () => Promise<void>;
  isLoading: boolean;
}

export const useNotifications = (options: UseNotificationsOptions = {}): UseNotificationsReturn => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [meta, setMeta] = useState<NotificationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<UseNotificationsOptions>(options);

  const fetchNotifications = useCallback(async (opts: UseNotificationsOptions) => {
    if (!isAuthenticated || !user?.id) return;

    setIsLoading(true);
    try {
      const response = await axiosServices.get('api/notificaciones/user', {
        params: {
          ...(opts.status ? { status: opts.status } : null),
          ...(opts.channel ? { channel: opts.channel } : null),
          ...(opts.perPage ? { per_page: opts.perPage } : null)
        }
      });
      const data = response.data as { success: boolean; data: Notification[]; meta: NotificationMeta };

      if (data.success) {
        setNotifications(data.data);
        setMeta(data.meta);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    fetchNotifications(currentOptions);
  }, [currentOptions, fetchNotifications]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    const echoInstance = initEcho();
    if (!echoInstance) {
      console.warn('Echo not available');
      return;
    }

    const channel = echoInstance.private(`user.${user.id}`);

    channel.listen('notification.received', (data: unknown) => {
      void (async () => {
        await fetchNotifications(currentOptions);
      })();
    });

    return () => {
      channel.leave();
    };
  }, [currentOptions, fetchNotifications, isAuthenticated, user?.id]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await axiosServices.patch(`api/notificaciones/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setMeta((prev) => prev ? { ...prev, unread_count: Math.max(0, prev.unread_count - 1) } : null);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, []);

  const markAsUnread = useCallback(async (id: string) => {
    try {
      await axiosServices.patch(`api/notificaciones/${id}/unread`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: null } : n))
      );
      setMeta((prev) => prev ? { ...prev, unread_count: prev.unread_count + 1 } : null);
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    try {
      await axiosServices.patch('api/notificaciones/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setMeta((prev) => prev ? { ...prev, unread_count: 0 } : null);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [isAuthenticated, user?.id]);

  const markAsSaved = useCallback(async (id: string) => {
    try {
      await axiosServices.patch(`api/notificaciones/${id}/save`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, saved_at: new Date().toISOString() } : n))
      );
    } catch (error) {
      console.error('Error marking notification as saved:', error);
    }
  }, []);

  const markAsUnsaved = useCallback(async (id: string) => {
    try {
      await axiosServices.patch(`api/notificaciones/${id}/unsave`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, saved_at: null } : n))
      );
    } catch (error) {
      console.error('Error marking notification as unsaved:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      await axiosServices.delete(`api/notificaciones/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setMeta((prev) => prev ? { ...prev, total: Math.max(0, prev.total - 1) } : null);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  const setFilters = useCallback((opts: UseNotificationsOptions) => {
    setCurrentOptions(opts);
  }, []);

  const refresh = useCallback(async () => {
    await fetchNotifications(currentOptions);
  }, [currentOptions, fetchNotifications]);

  const unreadCount = meta?.unread_count ?? notifications.filter((n) => !n.read_at).length;

  return {
    notifications,
    unreadCount,
    meta,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    markAsSaved,
    markAsUnsaved,
    deleteNotification,
    setFilters,
    refresh,
    isLoading
  };
};
